/**
 * Safe base64 helpers for bounded-core.
 *
 * Uses the global atob/btoa when available (browser, RN with polyfill),
 * falls back to the 'buffer' package (Node.js / SSR / RN without polyfill).
 *
 * These are used instead of bare `atob()`/`btoa()` so the core package
 * works in React Native without requiring the consumer to polyfill globals.
 *
 * Both directions are UTF-8-safe: bare btoa / Buffer('binary') are LATIN-1 and
 * throw (surrogate-pair emoji) or silently mangle (accents, CJK) any code point
 * > 0xFF, corrupting user text - e.g. an AI-query `prompt` - before it is sent.
 * safeBtoa base64s the UTF-8 bytes; safeAtob is its exact inverse, and the
 * realtime worker decodes the same bytes as UTF-8 (src/utf8-base64.ts). ASCII is
 * byte-identical either way, so JWT (decodeBase64Url) decoding is unaffected.
 */
import { Buffer } from 'buffer';
export function safeAtob(input) {
    if (typeof globalThis.atob === 'function') {
        const binary = globalThis.atob(input);
        const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
        return new TextDecoder().decode(bytes);
    }
    return Buffer.from(input, 'base64').toString('utf-8');
}
export function safeBtoa(input) {
    const bytes = new TextEncoder().encode(input);
    if (typeof globalThis.btoa === 'function') {
        let binary = '';
        for (let i = 0; i < bytes.length; i++)
            binary += String.fromCharCode(bytes[i]);
        return globalThis.btoa(binary);
    }
    return Buffer.from(bytes).toString('base64');
}
/**
 * Decode a base64url-encoded string (used by JWT payloads).
 * Normalises base64url → standard base64 before decoding.
 */
export function decodeBase64Url(input) {
    let b64 = input.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4;
    if (pad === 2)
        b64 += '==';
    else if (pad === 3)
        b64 += '=';
    return safeAtob(b64);
}
